import type { PolicyRuleType, ScopeKind } from '../../types/prioritization/permissions';
import type { FullEvalContext, PolicyReason, PolicyRule, ResourceType } from './types';

export type SupportedPolicyAction =
  | 'items.create'
  | 'items.triage'
  | 'items.edit'
  | 'workflow.create'
  | 'workflow.edit'
  | 'workflow.set_default'
  | 'space.invite'
  | 'space.member.add'
  | 'space.member.role.change'
  | 'space.member.remove'
  | 'type_patterns.revise';

export type PolicyConditionField =
  | 'actor.roleIds'
  | 'resource.workspaceId'
  | 'resource.lifecycleState'
  | 'resource.isLocked'
  | 'resource.isPublished'
  | 'context.phase';

export type PolicyConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'includes'
  | 'not_includes'
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'is_true'
  | 'is_false';

export interface AuthoredPolicyCondition {
  id: string;
  field: PolicyConditionField;
  operator: PolicyConditionOperator;
  value?: string | number | boolean | string[];
}

export interface AuthoredPolicyConfig {
  version: 1;
  targetActions: SupportedPolicyAction[];
  conditions: AuthoredPolicyCondition[];
  params: {
    approverRoles?: string[];
    allowedRoles?: string[];
    evidenceKey?: string;
    minParticipants?: number;
    startsAt?: string;
    endsAt?: string;
  };
  ui?: {
    priority?: number;
  };
}

export interface AuthoredPolicyRecord {
  id: string;
  name: string;
  description: string | null;
  scope_kind: ScopeKind;
  scope_id: string;
  rule_type: PolicyRuleType;
  rule_config: Record<string, unknown>;
  enabled: boolean;
  priority: number;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PolicyColumnSupport {
  priority: boolean;
}

export const SUPPORTED_POLICY_ACTION_OPTIONS: Array<{
  id: SupportedPolicyAction;
  label: string;
  resourceType: ResourceType;
}> = [
  { id: 'items.create', label: 'Create Items', resourceType: 'item' },
  { id: 'items.triage', label: 'Triage Items', resourceType: 'item' },
  { id: 'items.edit', label: 'Edit Items', resourceType: 'item' },
  { id: 'workflow.create', label: 'Create Workflows', resourceType: 'workflow' },
  { id: 'workflow.edit', label: 'Edit Workflows', resourceType: 'workflow' },
  { id: 'workflow.set_default', label: 'Set Workspace Default Workflow', resourceType: 'workflow' },
  { id: 'space.invite', label: 'Invite to Space', resourceType: 'space' },
  { id: 'space.member.add', label: 'Add Space Member', resourceType: 'space' },
  { id: 'space.member.role.change', label: 'Change Space Member Role', resourceType: 'space' },
  { id: 'space.member.remove', label: 'Remove Space Member', resourceType: 'space' },
  // Enforced by the database inside revise_type_pattern (migration 935).
  { id: 'type_patterns.revise', label: 'Change Type Patterns', resourceType: 'list' },
];

export const CONDITION_FIELD_OPTIONS: Array<{
  id: PolicyConditionField;
  label: string;
  valueType: 'string' | 'number' | 'boolean' | 'string[]';
  operators: PolicyConditionOperator[];
}> = [
  {
    id: 'actor.roleIds',
    label: 'Actor role',
    valueType: 'string[]',
    operators: ['includes', 'not_includes'],
  },
  {
    id: 'resource.workspaceId',
    label: 'Workspace',
    valueType: 'string',
    operators: ['equals', 'not_equals'],
  },
  {
    id: 'resource.lifecycleState',
    label: 'Lifecycle state',
    valueType: 'string',
    operators: ['equals', 'not_equals'],
  },
  {
    id: 'resource.isLocked',
    label: 'Resource is locked',
    valueType: 'boolean',
    operators: ['is_true', 'is_false'],
  },
  {
    id: 'resource.isPublished',
    label: 'Resource is published',
    valueType: 'boolean',
    operators: ['is_true', 'is_false'],
  },
  {
    id: 'context.phase',
    label: 'Current phase',
    valueType: 'string',
    operators: ['equals', 'not_equals'],
  },
];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

export function generateConditionId(): string {
  return `cond-${Math.random().toString(36).slice(2, 10)}`;
}

export function createDefaultAuthoredPolicyConfig(
  ruleType: PolicyRuleType = 'require_approval',
  action: SupportedPolicyAction = 'items.edit'
): AuthoredPolicyConfig {
  const base: AuthoredPolicyConfig = {
    version: 1,
    targetActions: [action],
    conditions: [],
    params: {},
    ui: { priority: 0 },
  };

  if (ruleType === 'require_approval') {
    base.params.approverRoles = ['workspace-admin'];
  }
  if (ruleType === 'restrict_role') {
    base.params.allowedRoles = ['workspace-admin'];
  }
  if (ruleType === 'require_evidence') {
    base.params.evidenceKey = 'evidenceProvided';
  }
  if (ruleType === 'require_quorum') {
    base.params.minParticipants = 2;
  }

  return base;
}

export function parseAuthoredPolicyConfig(
  ruleConfig: unknown,
  ruleType: PolicyRuleType
): AuthoredPolicyConfig {
  if (!isObject(ruleConfig)) {
    return createDefaultAuthoredPolicyConfig(ruleType);
  }

  if (ruleConfig.version === 1) {
    const targetActions = Array.isArray(ruleConfig.targetActions)
      ? ruleConfig.targetActions.filter((action): action is SupportedPolicyAction =>
          SUPPORTED_POLICY_ACTION_OPTIONS.some((option) => option.id === action)
        )
      : [];
    const parsedConditions = Array.isArray(ruleConfig.conditions)
      ? ruleConfig.conditions
          .filter(isObject)
          .map((condition) => ({
            id: typeof condition.id === 'string' ? condition.id : generateConditionId(),
            field: (condition.field as PolicyConditionField) ?? 'resource.workspaceId',
            operator: (condition.operator as PolicyConditionOperator) ?? 'equals',
            value: condition.value as AuthoredPolicyCondition['value'],
          }))
      : [];
    const params = isObject(ruleConfig.params) ? ruleConfig.params : {};
    const ui = isObject(ruleConfig.ui) ? ruleConfig.ui : {};

    return {
      version: 1,
      targetActions: targetActions.length > 0 ? targetActions : createDefaultAuthoredPolicyConfig(ruleType).targetActions,
      conditions: parsedConditions,
      params: {
        approverRoles: toStringArray(params.approverRoles),
        allowedRoles: toStringArray(params.allowedRoles),
        evidenceKey: typeof params.evidenceKey === 'string' ? params.evidenceKey : undefined,
        minParticipants:
          typeof params.minParticipants === 'number'
            ? params.minParticipants
            : typeof params.minParticipants === 'string'
              ? Number(params.minParticipants)
              : undefined,
        startsAt: typeof params.startsAt === 'string' ? params.startsAt : undefined,
        endsAt: typeof params.endsAt === 'string' ? params.endsAt : undefined,
      },
      ui: {
        priority:
          typeof ui.priority === 'number'
            ? ui.priority
            : typeof ui.priority === 'string'
              ? Number(ui.priority)
              : 0,
      },
    };
  }

  return createDefaultAuthoredPolicyConfig(ruleType);
}

export function getPolicyPriority(policy: Partial<AuthoredPolicyRecord>): number {
  if (typeof policy.priority === 'number' && Number.isFinite(policy.priority)) {
    return policy.priority;
  }

  const parsed = parseAuthoredPolicyConfig(policy.rule_config ?? {}, (policy.rule_type as PolicyRuleType) ?? 'require_approval');
  return parsed.ui?.priority ?? 0;
}

export function normalizePolicyRecord(
  row: Record<string, unknown>,
  columnSupport: PolicyColumnSupport
): AuthoredPolicyRecord {
  const scopeKind =
    readString(row.scope_kind) ??
    readString(row.scopeKind) ??
    readString(row.target_scope_kind) ??
    readString(row.targetScopeKind) ??
    'resource';
  const scopeId =
    readString(row.scope_id) ??
    readString(row.scopeId) ??
    readString(row.target_scope_id) ??
    readString(row.targetScopeId) ??
    '*';
  const ruleType =
    readString(row.rule_type) ??
    readString(row.ruleType) ??
    'require_approval';

  return {
    id: String(row.id),
    name: String(row.name ?? 'Untitled Policy'),
    description: typeof row.description === 'string' ? row.description : null,
    scope_kind: scopeKind as ScopeKind,
    scope_id: scopeId,
    rule_type: ruleType as PolicyRuleType,
    rule_config: isObject(row.rule_config) ? row.rule_config : {},
    enabled: readBoolean(row.enabled) ?? readBoolean(row.is_enabled) ?? true,
    priority:
      columnSupport.priority
        ? readNumber(row.priority) ?? 0
        : getPolicyPriority(row as Partial<AuthoredPolicyRecord>),
    created_by: readString(row.created_by) ?? readString(row.createdBy) ?? null,
    created_at: readString(row.created_at) ?? readString(row.createdAt),
    updated_at: readString(row.updated_at) ?? readString(row.updatedAt),
  };
}

/**
 * The current workspace's authored policies, as read from the database.
 *
 * Filled for everyone at session start and on every workspace switch
 * (authoredPolicyLoader.ts, called from userStore.loadPermissions), and again
 * whenever Settings → Policies saves or deletes a rule. Never kept in browser
 * storage: a rule lives in `public.policies` (build contract §2.2).
 */
let authoredPolicyCache: AuthoredPolicyRecord[] = [];
let authoredPolicyCacheWorkspaceId: string | null = null;

export function setAuthoredPolicyCache(policies: AuthoredPolicyRecord[], workspaceId: string | null = null): void {
  authoredPolicyCache = [...policies];
  authoredPolicyCacheWorkspaceId = workspaceId;
}

export function getAuthoredPolicyCache(): AuthoredPolicyRecord[] {
  return authoredPolicyCache;
}

/** Which workspace the cached policies belong to (null when none loaded). */
export function getAuthoredPolicyCacheWorkspaceId(): string | null {
  return authoredPolicyCacheWorkspaceId;
}

function getValueFromContext(field: PolicyConditionField, ctx: FullEvalContext): unknown {
  switch (field) {
    case 'actor.roleIds':
      return ctx.actor.roleIds;
    case 'resource.workspaceId':
      return ctx.resource.workspaceId ?? (ctx.actor as { currentWorkspaceId?: string }).currentWorkspaceId;
    case 'resource.lifecycleState':
      return ctx.resource.lifecycleState;
    case 'resource.isLocked':
      return ctx.resource.lockState?.isLocked ?? false;
    case 'resource.isPublished':
      return ctx.resource.isPublished ?? false;
    case 'context.phase':
      return ctx.context.phase;
    default:
      return undefined;
  }
}

function conditionMatches(condition: AuthoredPolicyCondition, ctx: FullEvalContext): boolean {
  const value = getValueFromContext(condition.field, ctx);

  switch (condition.operator) {
    case 'equals':
      return String(value ?? '') === String(condition.value ?? '');
    case 'not_equals':
      return String(value ?? '') !== String(condition.value ?? '');
    case 'includes':
      return Array.isArray(value)
        ? value.map(String).includes(String(condition.value ?? ''))
        : String(value ?? '').includes(String(condition.value ?? ''));
    case 'not_includes':
      return Array.isArray(value)
        ? !value.map(String).includes(String(condition.value ?? ''))
        : !String(value ?? '').includes(String(condition.value ?? ''));
    case 'greater_than_or_equal':
      return Number(value ?? 0) >= Number(condition.value ?? 0);
    case 'less_than_or_equal':
      return Number(value ?? 0) <= Number(condition.value ?? 0);
    case 'is_true':
      return Boolean(value) === true;
    case 'is_false':
      return Boolean(value) === false;
    default:
      return true;
  }
}

function policyConditionsMatch(config: AuthoredPolicyConfig, ctx: FullEvalContext): boolean {
  return config.conditions.every((condition) => conditionMatches(condition, ctx));
}

function actionToResourceTypes(actions: SupportedPolicyAction[]): ResourceType[] {
  return [...new Set(actions.map((action) =>
    SUPPORTED_POLICY_ACTION_OPTIONS.find((option) => option.id === action)?.resourceType ?? 'item'
  ))];
}

function scopeMatches(policy: AuthoredPolicyRecord, ctx: FullEvalContext): boolean {
  if (policy.scope_id === '*') return true;

  const actorCurrentWorkspaceId = (ctx.actor as { currentWorkspaceId?: string }).currentWorkspaceId;
  const actorCurrentTenantId = (ctx.actor as { currentTenantId?: string }).currentTenantId;
  const resourceScope = ctx.resource.scope;

  switch (policy.scope_kind) {
    case 'organization':
      return actorCurrentTenantId === policy.scope_id;
    case 'resource':
      return ctx.resource.workspaceId === policy.scope_id || actorCurrentWorkspaceId === policy.scope_id;
    case 'space':
      return ctx.resource.type === 'space' && ctx.resource.id === policy.scope_id;
    case 'item':
      return ctx.resource.type === 'item' && ctx.resource.id === policy.scope_id;
    case 'session':
      return ctx.resource.type === 'session' && ctx.resource.id === policy.scope_id;
    case 'process':
      return (ctx.resource.type === 'cycle' || ctx.resource.type === 'process') && ctx.resource.id === policy.scope_id;
    default:
      return resourceScope?.kind === policy.scope_kind && resourceScope.id === policy.scope_id;
  }
}

function actorHasRole(actorRoleIds: string[], roles?: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.some((role) => actorRoleIds.includes(role));
}

function readEvidenceValue(config: AuthoredPolicyConfig, ctx: FullEvalContext): boolean {
  const key = config.params.evidenceKey || 'evidenceProvided';
  const contextExtra = ctx.context.extra ?? {};
  const attributeValue = ctx.resource.attributes?.[key];
  return Boolean(contextExtra[key] ?? attributeValue);
}

function readParticipantCount(ctx: FullEvalContext): number {
  const extraCount = ctx.context.extra?.participantCount;
  const attributeCount = ctx.resource.attributes?.participantCount;
  return Number(extraCount ?? attributeCount ?? 0);
}

export function evaluatePolicyViolation(
  policy: AuthoredPolicyRecord,
  config: AuthoredPolicyConfig,
  ctx: FullEvalContext
): { violated: boolean; reasons: PolicyReason[] } {
  switch (policy.rule_type) {
    case 'require_approval': {
      const approverRoles = config.params.approverRoles ?? [];
      const approved = Boolean(ctx.context.extra?.approvalGranted);
      const actorIsApprover = actorHasRole(ctx.actor.roleIds, approverRoles);
      if (!approved && !actorIsApprover) {
        return {
          violated: true,
          reasons: [{
            code: 'APPROVAL_REQUIRED',
            data: { approverRoles, policyId: policy.id, policyName: policy.name },
          }],
        };
      }
      return { violated: false, reasons: [] };
    }
    case 'restrict_role': {
      const allowedRoles = config.params.allowedRoles ?? [];
      if (allowedRoles.length > 0 && !actorHasRole(ctx.actor.roleIds, allowedRoles)) {
        return {
          violated: true,
          reasons: [{
            code: 'ROLE_RESTRICTED',
            data: { allowedRoles, policyId: policy.id, policyName: policy.name },
          }],
        };
      }
      return { violated: false, reasons: [] };
    }
    case 'time_bound': {
      const now = ctx.context.now ?? new Date();
      const startsAt = config.params.startsAt ? new Date(config.params.startsAt) : null;
      const endsAt = config.params.endsAt ? new Date(config.params.endsAt) : null;
      if (startsAt && now < startsAt) {
        return {
          violated: true,
          reasons: [{
            code: 'NOT_YET_OPEN',
            data: { startsAt: startsAt.toISOString(), policyId: policy.id, policyName: policy.name },
          }],
        };
      }
      if (endsAt && now > endsAt) {
        return {
          violated: true,
          reasons: [{
            code: 'DEADLINE_PASSED',
            data: { endsAt: endsAt.toISOString(), policyId: policy.id, policyName: policy.name },
          }],
        };
      }
      return { violated: false, reasons: [] };
    }
    case 'require_evidence': {
      if (!readEvidenceValue(config, ctx)) {
        return {
          violated: true,
          reasons: [{
            code: 'MISSING_PREREQUISITE',
            data: {
              prerequisite: config.params.evidenceKey || 'evidenceProvided',
              policyId: policy.id,
              policyName: policy.name,
            },
          }],
        };
      }
      return { violated: false, reasons: [] };
    }
    case 'require_quorum': {
      const minParticipants = config.params.minParticipants ?? 0;
      const participantCount = readParticipantCount(ctx);
      if (participantCount < minParticipants) {
        return {
          violated: true,
          reasons: [{
            code: 'MISSING_PREREQUISITE',
            data: {
              prerequisite: 'participantCount',
              participantCount,
              minParticipants,
              policyId: policy.id,
              policyName: policy.name,
            },
          }],
        };
      }
      return { violated: false, reasons: [] };
    }
    default:
      return { violated: false, reasons: [] };
  }
}

export function buildPolicyPreview(policy: AuthoredPolicyRecord): string {
  const config = parseAuthoredPolicyConfig(policy.rule_config, policy.rule_type);
  const actionLabels = config.targetActions
    .map((action) => SUPPORTED_POLICY_ACTION_OPTIONS.find((option) => option.id === action)?.label ?? action)
    .join(', ');
  const scopeLabel = policy.scope_id === '*' ? `all ${policy.scope_kind}s` : `${policy.scope_kind} ${policy.scope_id}`;

  const base =
    policy.rule_type === 'require_approval'
      ? `Require approval before ${actionLabels} at ${scopeLabel}.`
      : policy.rule_type === 'restrict_role'
        ? `Only selected roles may ${actionLabels} at ${scopeLabel}.`
        : policy.rule_type === 'time_bound'
          ? `Only allow ${actionLabels} during the configured time window at ${scopeLabel}.`
          : policy.rule_type === 'require_evidence'
            ? `Require evidence before ${actionLabels} at ${scopeLabel}.`
            : policy.rule_type === 'require_quorum'
              ? `Require quorum before ${actionLabels} at ${scopeLabel}.`
              : `Apply ${policy.rule_type} to ${actionLabels} at ${scopeLabel}.`;

  const conditionText = config.conditions.length > 0
    ? ` Applies when ${config.conditions.map(describeCondition).join(' and ')}.`
    : '';

  return `${base}${conditionText}`;
}

export function describeCondition(condition: AuthoredPolicyCondition): string {
  const fieldLabel = CONDITION_FIELD_OPTIONS.find((option) => option.id === condition.field)?.label ?? condition.field;
  const formattedValue = Array.isArray(condition.value)
    ? condition.value.join(', ')
    : condition.value === undefined
      ? ''
      : String(condition.value);

  switch (condition.operator) {
    case 'equals':
      return `${fieldLabel} equals ${formattedValue}`;
    case 'not_equals':
      return `${fieldLabel} does not equal ${formattedValue}`;
    case 'includes':
      return `${fieldLabel} includes ${formattedValue}`;
    case 'not_includes':
      return `${fieldLabel} does not include ${formattedValue}`;
    case 'greater_than_or_equal':
      return `${fieldLabel} is at least ${formattedValue}`;
    case 'less_than_or_equal':
      return `${fieldLabel} is at most ${formattedValue}`;
    case 'is_true':
      return `${fieldLabel} is true`;
    case 'is_false':
      return `${fieldLabel} is false`;
    default:
      return fieldLabel;
  }
}

export function buildAuthoredPolicyRules(policies: AuthoredPolicyRecord[]): PolicyRule[] {
  return policies
    .filter((policy) => policy.enabled)
    .sort((a, b) => b.priority - a.priority)
    .flatMap((policy) => {
      const config = parseAuthoredPolicyConfig(policy.rule_config, policy.rule_type);
      const actions = config.targetActions;

      if (actions.length === 0) {
        return [];
      }

      const rule: PolicyRule = {
        id: `authored-policy:${policy.id}`,
        effect: 'deny',
        actions,
        resourceTypes: actionToResourceTypes(actions),
        description: buildPolicyPreview(policy),
        when: (ctx) => {
          if (!scopeMatches(policy, ctx)) return false;
          if (!policyConditionsMatch(config, ctx)) return false;
          return evaluatePolicyViolation(policy, config, ctx).violated;
        },
        reasons: (ctx) => evaluatePolicyViolation(policy, config, ctx).reasons,
      };

      return [rule];
    });
}

export function hasAuthoredPolicyRules(rules: PolicyRule[]): boolean {
  return rules.some((rule) => rule.id.startsWith('authored-policy:'));
}

